import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Overlay } from '@/components/ui/Overlay/Overlay';
import { Icon } from '@/components/ui/Icon/Icon';
import { MarkdownPreview } from '@/components/ui/MarkdownPreview/MarkdownPreview';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { useCabinDocument } from '@/services/documents';
import { nb } from '@/i18n/nb';
import s from './Hytteregler.module.css';

const SUBCARDS = [
  { slug: 'ordensregler', title: nb.documents.regler.subcards.ordensregler },
  { slug: 'avreiserutiner', title: nb.documents.regler.subcards.avreiserutiner },
  { slug: 'stengerutiner', title: nb.documents.regler.subcards.stengerutiner },
] as const;

/*
 * Hytteregler og -rutiner — a nested module. The overlay opens to three sub-cards;
 * tapping one shows that document's DB-backed content. The overlay title + the shared
 * <Overlay onBack> track the level, so the back button / Esc step sub-card → overview →
 * landing (Jon's Iter-5 decision). It renders its own <Overlay> to own the sub-state.
 */
export function Hytteregler({ onClose }: { onClose: () => void }) {
  const [sub, setSub] = useState<number | null>(null);
  const active = sub !== null ? SUBCARDS[sub] : null;

  return (
    <Overlay
      title={active ? active.title : nb.modules.regler.title}
      onClose={onClose}
      onBack={active ? () => setSub(null) : undefined}
    >
      {active ? (
        <HyttereglerSubcard slug={active.slug} />
      ) : (
        <div className={s.grid}>
          {SUBCARDS.map((card, i) => (
            <button key={card.slug} type="button" className={s.subcard} onClick={() => setSub(i)}>
              <Icon name="list" size={26} className={s.subcardIcon} />
              <span className={s.subcardLabel}>{card.title}</span>
            </button>
          ))}
        </div>
      )}
    </Overlay>
  );
}

function HyttereglerSubcard({ slug }: { slug: string }) {
  const navigate = useNavigate();
  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const { data: doc, isLoading, isError } = useCabinDocument(slug);
  const containerRef = useRef<HTMLDivElement>(null);

  // Move focus into the freshly opened sub-card so keyboard / screen-reader users land
  // on the new content rather than being stranded on the now-unmounted sub-card button.
  useEffect(() => {
    containerRef.current?.focus();
  }, []);

  const canEdit = editMode && user?.role === 'admin';

  return (
    <div ref={containerRef} tabIndex={-1} className={s.subcardView}>
      {isLoading && <p className={s.status} aria-live="polite" />}
      {isError && <p className={s.status}>{nb.documents.loadError}</p>}
      {doc && (
        <>
          {canEdit && (
            <div className={s.editRow}>
              <button
                type="button"
                className={s.editBtn}
                onClick={() => navigate(`/admin/innhold/${slug}`, { state: { from: 'public' } })}
              >
                <Icon name="pencil" size={16} /> {nb.documents.regler.editSubcard}
              </button>
            </div>
          )}
          <MarkdownPreview markdown={doc.content} />
        </>
      )}
    </div>
  );
}
